import { useEffect, useRef } from 'react';
import type { MapProperty } from './types';
import type { DealFlag } from './dealFlags';
import { distressLevel } from './markerIcons';

const money = (v?: number | null) =>
  !v ? 'Unpriced' : v >= 1_000_000 ? '$' + (v / 1_000_000).toFixed(2) + 'M'
    : v >= 1_000 ? '$' + (v / 1_000).toFixed(0) + 'K' : '$' + v.toLocaleString();

const titleCase = (s?: string) => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');

// One shared pill spec for every card badge, so the deal / distress / stale tags
// read as a consistent set — only the color varies by meaning.
const BADGE = 'inline-flex items-center leading-none text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1';

interface Props {
  items: MapProperty[];
  hoveredId: string | null;
  selectedId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (p: MapProperty) => void;
  // Comp-tray selection (checkboxes)
  selectedIds: Set<string>;
  onToggleSelect: (p: MapProperty) => void;
  onSelectAllVisible: () => void;
  onClearVisible: () => void;
  dealFlags?: Map<string, DealFlag>;
}

export default function MapResultsList({
  items, hoveredId, selectedId, onHover, onSelect,
  selectedIds, onToggleSelect, onSelectAllVisible, onClearVisible, dealFlags,
}: Props) {
  const activeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (hoveredId && activeRef.current) activeRef.current.scrollIntoView({ block: 'nearest' });
  }, [hoveredId]);

  if (items.length === 0) {
    return <div className="p-6 text-center text-gray-500 text-sm">No properties match</div>;
  }
  const CAP = 200;
  const shown = items.slice(0, CAP);
  const shownSelected = shown.filter((p) => selectedIds.has(p.id)).length;
  const allShownSelected = shownSelected === shown.length;

  return (
    <div>
      {/* Select-all header */}
      <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur border-b border-gray-200 text-xs">
        <input
          type="checkbox"
          className="h-4 w-4 accent-primary-600 cursor-pointer"
          checked={allShownSelected}
          ref={(el) => { if (el) el.indeterminate = shownSelected > 0 && !allShownSelected; }}
          onChange={() => (allShownSelected ? onClearVisible() : onSelectAllVisible())}
          aria-label="Select all visible"
        />
        <span className="font-medium text-gray-500">
          {shownSelected > 0 ? `${shownSelected} selected for comps` : `Select all ${shown.length}`}
        </span>
      </div>

      <div>
        {shown.map((p) => {
          const active = p.id === hoveredId || p.id === selectedId;
          const checked = selectedIds.has(p.id);
          const psf = p.price && p.building_size ? Math.round(p.price / p.building_size) : null;
          const flag = dealFlags?.get(p.id);
          const distress = distressLevel(p);
          // Street View fallback images live under the `street-view/` storage path,
          // so the URL alone tells us to tag them (attribution + honesty).
          const isStreetView = !!p.primary_photo_url && p.primary_photo_url.includes('/street-view/');
          const specs = [
            p.building_size ? `${p.building_size.toLocaleString()} SF` : null,
            titleCase(p.property_type),
            p.year_built ? `Built ${p.year_built}` : null,
          ].filter(Boolean).join('  ·  ');
          return (
            <div
              key={p.id}
              ref={active ? activeRef : undefined}
              onMouseEnter={() => onHover(p.id)}
              onMouseLeave={() => onHover(null)}
              onClick={() => onSelect(p)}
              className={`group relative cursor-pointer border-b border-gray-100 transition-colors ${checked ? 'bg-primary-50/50' : active ? 'bg-primary-50/70' : 'bg-white hover:bg-gray-50/80'}`}
            >
              <div className="flex gap-3 p-3">
                {/* Photo */}
                <div className="relative w-[150px] h-[112px] shrink-0 rounded-lg bg-gray-100 overflow-hidden ring-1 ring-gray-200/70">
                  {p.primary_photo_url
                    ? <img src={p.primary_photo_url} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                    : <div className="w-full h-full flex items-center justify-center text-gray-300 text-[11px]">No photo</div>}
                  {isStreetView ? (
                    <span className="absolute bottom-1.5 left-1.5 bg-black/65 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">Street View</span>
                  ) : p.photo_count ? (
                    <span className="absolute bottom-1.5 left-1.5 bg-black/65 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">1/{p.photo_count}</span>
                  ) : null}
                  {p.match_score != null ? (
                    <span className="absolute top-1.5 left-1.5 bg-primary-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-sm">{p.match_score}% match</span>
                  ) : null}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 pr-7">
                  <p className="text-[15px] font-semibold text-gray-900 leading-snug truncate">{p.address || 'No address'}</p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{[p.city, p.state, p.zip].filter(Boolean).join(', ')}</p>
                  {specs && <p className="text-[11px] text-gray-500 mt-1.5 truncate">{specs}</p>}

                  {(flag || distress) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      {flag?.belowMarketPct != null && (
                        <span className={`${BADGE} bg-success-wash text-success ring-success/30`}>{flag.belowMarketPct}% below mkt</span>
                      )}
                      {distress && (
                        <span className={`${BADGE} ${distress === 'past_due' ? 'bg-primary-100 text-primary-500 ring-primary-200' : 'bg-warning-wash text-warning ring-warning/30'}`}>
                          {distress === 'past_due' ? 'Loan past due' : 'Loan maturing'}
                        </span>
                      )}
                      {flag?.stale && (
                        <span className={`${BADGE} bg-gray-50 text-gray-500 ring-gray-200`}>Stale</span>
                      )}
                    </div>
                  )}

                  <div className="mt-2 flex items-baseline justify-between gap-2">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[17px] font-bold text-gray-900 tracking-tight">{money(p.price)}</span>
                      {psf ? <span className="text-xs text-gray-400">${psf}/SF</span> : null}
                    </div>
                    <a
                      href={`/data-hub/${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="shrink-0 text-[11px] font-semibold text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity hover:underline"
                    >View →</a>
                  </div>
                </div>
              </div>

              {/* Circle select (comp tray) — top-right */}
              <label
                onClick={(e) => e.stopPropagation()}
                className="absolute top-3 right-3 cursor-pointer"
                title={checked ? 'Remove from comps' : 'Add to comps'}
              >
                <input type="checkbox" className="sr-only peer" checked={checked} onChange={() => onToggleSelect(p)} aria-label={`Select ${p.address || 'property'} for comps`} />
                <span className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-gray-300 bg-white/90 shadow-sm text-transparent peer-checked:bg-gray-900 peer-checked:border-gray-900 peer-checked:text-white transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                </span>
              </label>
            </div>
          );
        })}
        {items.length > CAP && (
          <div className="p-4 text-center text-xs text-gray-400 border-t border-gray-100">
            Showing {CAP} of {items.length.toLocaleString()} · zoom in, draw an area, or search to narrow
          </div>
        )}
      </div>
    </div>
  );
}
